import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiGetWebsiteTextSettings } from '../api/apiTypography';

/**
 * Typography tokens from product-service `/website-text-settings` (public GET).
 *
 * Base tokens (H1–P3, Button, Label, Overlay) carry their own values.
 * Component tokens (featured.heading, accordion.button, …) link to a base via
 * `linked_setting_key`, or carry their own values when detached.
 *
 * Components consume a token with `styleFor('key')`, which returns an MUI
 * `sx`-compatible object. There is a built-in fallback so the UI never renders
 * broken before the fetch completes or if a key isn't seeded yet.
 */

export interface SiteTextSetting {
  id: number;
  setting_key: string;
  token_type: 'base' | 'component';
  linked_setting_key: string | null;
  font_family: string;
  font_size: string;
  font_size_mobile: string;
  font_size_unit: string;
  letter_spacing: string;
  letter_spacing_unit: string;
  line_height: string;
  line_height_unit: string;
  font_weight: number;
  text_transform: string | null;
  is_bold: boolean;
  is_italic: boolean;
  is_underline: boolean;
  text_color_hex: string;
  custom_font_id: number | null;
  custom_font_name?: string | null;
  custom_font_file_url?: string | null;
}

interface Resolved {
  family: string;
  size: number; mobile: number; unit: string;
  weight: number; italic: boolean; underline: boolean;
  transform: string; ls: number; lsUnit: string; lh: number; lhUnit: string;
  color: string;
}

type StyleOptions = { color?: boolean }; // color is opt-in (off by default)

interface TypographyCtx {
  ready: boolean;
  styleFor: (key: string, opts?: StyleOptions) => Record<string, unknown>;
}

// ─── Built-in fallback (Font Guide values) ──────────────────────────────────
const F = (family: string, size: number, mobile: number, weight: number, extra: Partial<Resolved> = {}): Resolved => ({
  family, size, mobile, unit: 'px', weight, italic: false, underline: false,
  transform: 'none', ls: 0, lsUnit: 'px', lh: 0, lhUnit: '', color: '#000000', ...extra,
});
const BASE_FALLBACK: Record<string, Resolved> = {
  H1: F('Perpetua', 48, 38, 400), H2: F('Perpetua', 44, 35, 400),
  H3: F('Perpetua', 40, 32, 400), H4: F('Perpetua', 38, 30, 400),
  H5: F('Inter', 20, 16, 700), H6: F('Inter', 18, 14, 700),
  P1: F('Inter', 20, 16, 400, { lh: 1.6 }), P2: F('Inter', 18, 14, 400, { lh: 1.6 }),
  P3: F('Inter', 16, 13, 400, { lh: 1.6 }),
  Button: F('Inter', 14, 14, 600, { transform: 'uppercase', ls: 0.5 }),
  Label: F('Inter', 12, 12, 700, { transform: 'uppercase', ls: 1 }),
  Overlay: F('Perpetua', 40, 32, 300, { color: '#FFFFFF' }),
};
// Component key → base level it falls back to (until seeded / for graceful default).
const COMPONENT_LINKS: Record<string, string> = {
  'featured.label': 'P2', 'featured.heading': 'H2', 'featured.description': 'P1', 'featured.button': 'Button',
  'accordion.heading': 'H2', 'accordion.button': 'H4', 'accordion.collectionName': 'H5',
  'grid.sectionHeader': 'H3', 'tile.overlayLabel': 'Overlay', 'tile.collectionName': 'H5',
  'section.heading': 'H3', 'section.body': 'P1',
  'listing.title': 'H3', 'listing.intro': 'P2', 'filter.sectionLabel': 'H5',
  'product.name': 'H5', 'product.colour': 'P3', 'product.size': 'P3',
};

const num = (v: string | number | null | undefined) => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const familyStack = (name: string) =>
  /perpetua|garamond|times|serif/i.test(name || '') ? `'${name}', Georgia, serif` : `'${name}', system-ui, sans-serif`;

const fromRow = (r: SiteTextSetting): Resolved => ({
  family: r.custom_font_id && r.custom_font_name ? r.custom_font_name : r.font_family,
  size: num(r.font_size) || 16,
  mobile: num(r.font_size_mobile) || num(r.font_size) || 16,
  unit: r.font_size_unit || 'px',
  weight: r.is_bold ? 700 : (r.font_weight || 400),
  italic: !!r.is_italic, underline: !!r.is_underline,
  transform: r.text_transform || 'none',
  ls: num(r.letter_spacing), lsUnit: r.letter_spacing_unit || 'px',
  lh: num(r.line_height), lhUnit: r.line_height_unit || '',
  color: r.text_color_hex || '#000000',
});

const toSx = (r: Resolved, opts?: StyleOptions): Record<string, unknown> => ({
  fontFamily: familyStack(r.family),
  fontSize: { xs: `${r.mobile}${r.unit}`, md: `${r.size}${r.unit}` },
  fontWeight: r.weight,
  fontStyle: r.italic ? 'italic' : 'normal',
  textDecoration: r.underline ? 'underline' : 'none',
  textTransform: r.transform || 'none',
  letterSpacing: r.ls ? `${r.ls}${r.lsUnit || 'px'}` : 'normal',
  lineHeight: r.lh > 0 ? (r.lhUnit ? `${r.lh}${r.lhUnit}` : r.lh) : 'normal',
  ...(opts?.color ? { color: r.color } : {}),
});

const Ctx = createContext<TypographyCtx>({ ready: false, styleFor: () => ({}) });

export const TypographyProvider = ({ children }: { children: React.ReactNode }) => {
  const [byKey, setByKey] = useState<Record<string, SiteTextSetting>>({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGetWebsiteTextSettings();
        const grouped = data?.grouped_settings || {};
        const map: Record<string, SiteTextSetting> = {};
        Object.values(grouped).forEach((rows) => (rows as SiteTextSetting[]).forEach((r) => { map[r.setting_key] = r; }));
        if (!cancelled) { setByKey(map); setReady(true); }
      } catch (e) {
        // Non-fatal: components fall back to the built-in defaults.
        console.warn('Typography settings unavailable, using defaults', e);
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // @font-face for any uploaded custom fonts referenced by the tokens.
  const fontFaceCss = useMemo(() => {
    const seen = new Set<string>();
    return Object.values(byKey)
      .filter((r) => r.custom_font_name && r.custom_font_file_url && !seen.has(r.custom_font_name) && seen.add(r.custom_font_name!))
      .map((r) => `@font-face{font-family:'${r.custom_font_name}';src:url('${r.custom_font_file_url}');font-display:swap;}`)
      .join('\n');
  }, [byKey]);

  const styleFor = useMemo(() => (key: string, opts?: StyleOptions): Record<string, unknown> => {
    const live = byKey[key];
    if (live) {
      const eff = live.linked_setting_key ? (byKey[live.linked_setting_key] || live) : live;
      return toSx(fromRow(eff), opts);
    }
    // fallback: base key directly, or component key via its default link
    const fb = BASE_FALLBACK[key] || BASE_FALLBACK[COMPONENT_LINKS[key]];
    return fb ? toSx(fb, opts) : {};
  }, [byKey]);

  return (
    <Ctx.Provider value={{ ready, styleFor }}>
      {fontFaceCss ? <style>{fontFaceCss}</style> : null}
      {children}
    </Ctx.Provider>
  );
};

/** Hook: `const { styleFor } = useTypography();` then `sx={styleFor('featured.heading')}`. */
export const useTypography = () => useContext(Ctx);

export default TypographyProvider;
